// File Metadata:
// Filename: Camera.js
// Version: 1.00
// Created: 2022-10-26 22:49:08
// Last Modified: 2025-04-01 16:55:19
// Authors: [Your Name]
// Description: A brief summary of what this file does

import React, { useState, useEffect } from 'react'
import Camera from 'react-html5-camera-photo'
import 'react-html5-camera-photo/build/css/index.css'
import { Box, IconButton, Alert, Button, Stack, Slider, Typography, Grid } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FlipCameraAndroid from '@mui/icons-material/FlipCameraAndroid'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import CheckIcon from '@mui/icons-material/Check'
import ReplayIcon from '@mui/icons-material/Replay'
import BrightnessIcon from '@mui/icons-material/Brightness6'
import TimerIcon from '@mui/icons-material/Timer'
import GridOnIcon from '@mui/icons-material/GridOn'
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'

const CameraWrapper = ({ onTakePhoto, onClose, onError }) => {
    const [preview, setPreview] = useState(null)
    const [quality, setQuality] = useState(0.8)
    const [rotation, setRotation] = useState(0)
    const [brightness, setBrightness] = useState(1)
    const [showGrid, setShowGrid] = useState(false)
    const [timer, setTimer] = useState(0)
    const [isCountingDown, setIsCountingDown] = useState(false)
    const [cameraDevices, setCameraDevices] = useState([])
    const [selectedCamera, setSelectedCamera] = useState(null)

    useEffect(() => {
        if (preview) {
            updatePreview()
        }
    }, [quality, rotation, brightness])

    useEffect(() => {
        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                const cameras = devices.filter(device => device.kind === 'videoinput')
                setCameraDevices(cameras)
                if (cameras.length > 0) setSelectedCamera(cameras[0].deviceId)
            })
    }, [])

    const handleCameraError = (error) => {
        console.error('Camera Error:', error)
        onError?.(error.message || 'Camera initialization failed')
    }

    const updatePreview = () => {
        const canvas = document.createElement('canvas')
        const img = new Image()

        img.onload = () => {
            const originalWidth = img.width
            const originalHeight = img.height

            // Set canvas size based on rotation
            canvas.width = rotation % 180 === 0 ? originalWidth : originalHeight
            canvas.height = rotation % 180 === 0 ? originalHeight : originalWidth

            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            // Center point for rotation
            ctx.save()
            ctx.translate(canvas.width / 2, canvas.height / 2)
            ctx.rotate((rotation * Math.PI) / 180)
            ctx.translate(-originalWidth / 2, -originalHeight / 2)

            // Apply brightness
            ctx.filter = `brightness(${brightness})`

            // Draw the image
            ctx.drawImage(img, 0, 0)
            ctx.restore()

            // Convert to data URL with quality setting
            setPreview(canvas.toDataURL('image/jpeg', quality))
        }

        // Load original image if this is first edit, otherwise load from stored original
        img.src = window._originalImage || preview
        if (!window._originalImage) {
            window._originalImage = preview // Store original for future edits
        }
    }

    const handleTakePhoto = (dataUri) => {
        window._originalImage = dataUri // Store original image
        setPreview(dataUri)
    }

    const handleCameraSwitch = async () => {
        const currentIndex = cameraDevices.findIndex(d => d.deviceId === selectedCamera)
        const nextCamera = cameraDevices[(currentIndex + 1) % cameraDevices.length]
        setSelectedCamera(nextCamera.deviceId)
    }

    const startTimerCapture = () => {
        if (timer > 0) {
            setIsCountingDown(true)
            let countdown = timer
            const interval = setInterval(() => {
                countdown--
                if (countdown === 0) {
                    clearInterval(interval)
                    handleTakePhoto()
                    setIsCountingDown(false)
                }
            }, 1000)
        } else {
            handleTakePhoto()
        }
    }

    // Clean up stored original on close or confirm
    const handleConfirm = () => {
        if (preview) {
            onTakePhoto(preview)
            window._originalImage = null
            onClose?.()
        }
    }

    useEffect(() => {
        return () => {
            window._originalImage = null // Cleanup on unmount
        }
    }, [])

    if (preview) {
        return (
            <Box sx={{ 
                position: 'relative', 
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto'
            }}>
                <Stack spacing={2} sx={{ p: 2 }}>
                    <Box sx={{ 
                        position: 'relative',
                        width: '100%',
                        maxHeight: '50vh',
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <img 
                            src={preview} 
                            alt="Preview" 
                            style={{ 
                                maxWidth: '100%',
                                maxHeight: '50vh',
                                objectFit: 'contain'
                            }} 
                        />
                    </Box>
                    
                    <Typography>Quality</Typography>
                    <Slider 
                        value={quality} 
                        onChange={(e, v) => setQuality(v)}
                        min={0.1}
                        max={1}
                        step={0.1}
                    />

                    <Typography>Rotation</Typography>
                    <IconButton onClick={() => setRotation(r => (r + 90) % 360)}>
                        <RotateRightIcon />
                    </IconButton>

                    <Typography>Brightness</Typography>
                    <Slider
                        value={brightness}
                        onChange={(e, v) => setBrightness(v)}
                        min={0.5}
                        max={1.5}
                        step={0.1}
                        icon={<BrightnessIcon />}
                    />

                    <Stack direction="row" spacing={2}>
                        <Button 
                            startIcon={<ReplayIcon />}
                            onClick={() => setPreview(null)}
                            fullWidth
                        >
                            Retake
                        </Button>
                        <Button
                            startIcon={<CheckIcon />}
                            onClick={handleConfirm}
                            variant="contained"
                            fullWidth
                        >
                            Use Photo
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        )
    }

    return (
        <Box sx={{ 
            position: 'relative',
            width: '100%',
            backgroundColor: '#000'
        }}>
            {/* Close button */}
            <IconButton
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    zIndex: 2,
                    color: 'white'
                }}
                onClick={onClose}
            >
                <CloseIcon />
            </IconButton>

            <Camera
                onTakePhoto={handleTakePhoto}
                onCameraError={handleCameraError}
                isFullscreen={false}
                idealFacingMode="environment"
                imageType="jpg"
                imageCompression={0.8}
                isMaxResolution={true}
                isImageMirror={false}
                isSilentMode={true}
                sizeFactor={1}
            />

            {/* Camera Controls */}
            <Grid container spacing={2} alignItems="center" sx={{ p: 2, bgcolor: 'background.paper' }}>
                {cameraDevices.length > 1 && (
                    <Grid item>
                        <IconButton onClick={handleCameraSwitch}>
                            <FlipCameraAndroid />
                        </IconButton>
                    </Grid>
                )}
                <Grid item>
                    <IconButton onClick={() => setShowGrid(!showGrid)}>
                        <GridOnIcon />
                    </IconButton>
                </Grid>
                <Grid item>
                    <IconButton onClick={() => setTimer(prev => (prev + 3) % 10)}>
                        <TimerIcon />
                        {timer > 0 && (
                            <Typography variant="caption" sx={{ position: 'absolute' }}>
                                {timer}s
                            </Typography>
                        )}
                    </IconButton>
                </Grid>
                {/* ...existing controls... */}
            </Grid>
        </Box>
    )
}

// Error fallback component
export const CameraError = ({ message, onRetry, onClose }) => (
    <Box p={3} textAlign="center">
        <Alert 
            severity="error"
            action={
                <>
                    {onRetry && (
                        <Button color="inherit" size="small" onClick={onRetry}>
                            Retry
                        </Button>
                    )}
                    {onClose && (
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </>
            }
        >
            {message || 'Failed to access camera'}
        </Alert>
    </Box>
)

export default CameraWrapper
